// Colour maths + derived CSS tokens + WCAG checks. Mirrors backend/appearance/colors.py.
export const WHITE = "#FFFFFF";
const BLACK = "#000000";
const HEX = /^#[0-9A-Fa-f]{6}$/;

export const EDITABLE_TOKENS = [
  "primary", "brandAccent", "pageBackground", "surface", "sidebarBackground", "textPrimary", "border",
];
export const LABELS = {
  primary: "Primary colour", brandAccent: "Accent colour", pageBackground: "Page background",
  surface: "Cards and tables", sidebarBackground: "Sidebar", textPrimary: "Text", border: "Borders",
};

const rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const toHex = (c) => "#" + c.map((x) => Math.max(0, Math.min(255, Math.floor(x + 0.5))).toString(16).padStart(2, "0")).join("").toUpperCase();
export const validHex = (v) => typeof v === "string" && HEX.test(v);

export function luminance(h) {
  const [r, g, b] = rgb(h).map((c) => { c /= 255; return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrast(a, b) {
  const la = luminance(a), lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
export function mix(a, b, t) {
  const ra = rgb(a), rb = rgb(b);
  return toHex(ra.map((x, i) => x * t + rb[i] * (1 - t)));
}
const minContrast = (fg, bgs) => Math.min(...bgs.map((b) => contrast(fg, b)));

export function adjust(fg, bgs, ratio) {
  if (minContrast(fg, bgs) >= ratio) return fg;
  const avg = bgs.reduce((s, b) => s + luminance(b), 0) / bgs.length;
  const target = avg > 0.4 ? BLACK : WHITE;
  for (let i = 1; i <= 20; i++) {
    const c = mix(target, fg, i * 0.05);
    if (minContrast(c, bgs) >= ratio) return c;
  }
  return target;
}
function lightenTo(h, lum) {
  if (luminance(h) >= lum) return h;
  for (let i = 1; i <= 20; i++) {
    const c = mix(WHITE, h, i * 0.05);
    if (luminance(c) >= lum) return c;
  }
  return WHITE;
}

export function applyOverrides(tokens, overrides) {
  const t = { ...tokens };
  const o = overrides || {};
  Object.entries(o).forEach(([k, v]) => { if (EDITABLE_TOKENS.includes(k) && validHex(v)) t[k] = v.toUpperCase(); });
  if (o.primary) t.focusRing = t.primary;
  if (o.primary || o.sidebarBackground) t.selectedBackground = mix(t.primary, t.sidebarBackground, 0.18);
  if (o.textPrimary || o.surface) t.textSecondary = mix(t.textPrimary, t.surface, 0.72);
  return t;
}

const LIGHT_MIN = { surface: 0.7, pageBackground: 0.7, sidebarBackground: 0.6 };

export function autofix(t) {
  const f = { ...t };
  Object.entries(LIGHT_MIN).forEach(([k, lum]) => { f[k] = lightenTo(f[k], lum); });
  f.textPrimary = adjust(f.textPrimary, [f.surface, f.pageBackground, f.sidebarBackground], 4.5);
  f.primary = adjust(f.primary, [WHITE, f.surface, f.pageBackground], 4.5);
  f.selectedBackground = adjust(f.selectedBackground, [f.textPrimary], 4.5);
  return f;
}

export function check(t) {
  const issues = [];
  const fixed = autofix(t);
  Object.entries(LIGHT_MIN).forEach(([k, lum]) => {
    if (luminance(t[k]) < lum) issues.push({ code: `${k}_too_dark`, tokens: [k], message: `${LABELS[k]} is too dark for light mode.`, fix: { [k]: fixed[k] } });
  });
  let r = minContrast(t.textPrimary, [t.surface, t.pageBackground, t.sidebarBackground]);
  if (r < 4.5) issues.push({ code: "text_contrast", tokens: ["textPrimary"], ratio: r, required: 4.5, message: `Text contrast is ${r.toFixed(1)}:1 on your backgrounds; it needs at least 4.5:1.`, fix: { textPrimary: fixed.textPrimary } });
  r = contrast(WHITE, t.primary);
  if (r < 4.5) issues.push({ code: "primary_button_contrast", tokens: ["primary"], ratio: r, required: 4.5, message: `White button text on the primary colour is ${r.toFixed(1)}:1; it needs at least 4.5:1.`, fix: { primary: fixed.primary } });
  r = minContrast(t.primary, [t.surface, t.pageBackground]);
  if (r < 3 && contrast(WHITE, t.primary) >= 4.5) issues.push({ code: "primary_visibility", tokens: ["primary"], ratio: r, required: 3, message: `Primary colour is ${r.toFixed(1)}:1 against the background; focus outlines need 3:1.`, fix: { primary: fixed.primary } });
  return issues;
}

export function derive(t) {
  const p = t.primary, s = t.surface, pg = t.pageBackground, sb = t.sidebarBackground, tx = t.textPrimary, b = t.border;
  const sel = adjust(t.selectedBackground, [tx], 4.5);
  const subtle = mix(sb, s, 0.5);
  const t2 = adjust(t.textSecondary, [s, pg, sb, subtle], 4.5);
  const ptext = adjust(p, [s, pg, sel], 4.5);
  const hover = luminance(p) > 0.02 ? mix(BLACK, p, 0.18) : mix(WHITE, p, 0.18);
  return {
    primary: p, "primary-hover": hover, "primary-text": ptext,
    "primary-deep": adjust(mix(tx, ptext, 0.35), [s, pg, sel], 4.5),
    "primary-soft": mix(p, s, 0.07), "primary-line": mix(p, b, 0.25),
    "primary-line-strong": adjust(mix(p, b, 0.55), [s], 3.0),
    accent: t.brandAccent, page: pg, surface: s, sidebar: sb, selected: sel, subtle, muted: sb, "muted-strong": b,
    text: tx, "text-body": mix(tx, t2, 0.5), "text-2": t2,
    "text-muted": adjust(mix(t2, s, 0.8), [s, pg, subtle], 4.5), "text-faint": mix(t2, s, 0.45),
    border: b, "border-subtle": mix(b, s, 0.5),
    "input-border": adjust(mix(t2, b, 0.4), [s, pg], 3.0),
    "border-strong": adjust(mix(t2, b, 0.85), [s, pg], 3.0),
    focus: adjust(t.focusRing, [s, pg, sb], 3.0),
    warning: adjust(t.warning, [s, pg], 4.5), danger: adjust(t.danger, [s, pg], 4.5),
    "on-primary": WHITE, "on-primary-soft": adjust(mix(WHITE, p, 0.85), [p], 4.5),
    "od-text-body": adjust(mix(WHITE, p, 0.92), [p], 4.5), "od-text-2": adjust(mix(WHITE, p, 0.85), [p], 4.5),
    "od-text-muted": adjust(mix(WHITE, p, 0.78), [p], 4.5), "od-text-faint": mix(WHITE, p, 0.6),
  };
}

const SCOPED = { body: "text-body", 2: "text-2", muted: "text-muted", faint: "text-faint" };
const triplet = (h) => rgb(h).join(" ");

// hex token map -> CSS custom properties (RGB triplets) incl. the scoped grey text vars
export function cssVars(vars) {
  const out = {};
  Object.entries(vars).forEach(([k, v]) => { if (validHex(v)) out[`--le-${k}`] = triplet(v); });
  Object.entries(SCOPED).forEach(([k, src]) => { if (vars[src]) out[`--le-t-${k}`] = triplet(vars[src]); });
  return out;
}
